import { ActionsMap } from '@packages/context/lib/action';
import { NotificationServiceProviderImpl } from '@packages/notifications/lib/NotificationServiceProvider';
import { RouterServiceProviderImpl } from '@packages/router/lib/RouterServiceProvider';
import { Record } from 'immutable';
import { Action } from './action';

export interface ContextServices<T extends keyof NonNullable<any>> {
    router: RouterServiceProviderImpl;
    notify: NotificationServiceProviderImpl;
    actions: ActionsMap<T>;
}

export type ContextEventType<S, A> = (
    state: Record<S>,
    action: Action<any, keyof A>,
    services?: ContextServices<keyof A>,
) => Record<S>;

export interface ContextOptions<S, A> {
    /**
     * Define Context name used for logs and code organization
     */
    name: string;

    /**
     * Define Context name
     */
    contextName: string;

    /**
     * Turn on/off debug messages
     */
    debug:
        | boolean
        | {
              actions?: boolean;
              events?: boolean;
              misc?: boolean;
          };

    /**
     * Default state of the context
     */
    initialState: Record.Factory<S>;

    stateExtend(state: Record<S>, props: any): Record<S>;

    /**
     * On change event
     * @param state
     */
    onChange(state: Record<S>): void;

    /**
     * Detect if the context value should change when new value it's passed
     * @param prevState
     * @param nextState
     */
    shouldUpdate(prevState: Record<S>, nextState: Record<S>): boolean;

    /**
     * Actions that will be able to be called into our context
     */
    actions: Partial<ActionsMap<keyof A>>;

    events: {
        [key: string]: ContextEventType<S, A>;
    };
}

const INITIAL_STATE = Record({});

export const createContextOptions = <S, A>(
    options?: Partial<ContextOptions<S, A>>,
): ContextOptions<S, A> & {
    debug: {
        actions?: boolean;
        events?: boolean;
        misc?: boolean;
    };
} => {
    const options$: Partial<ContextOptions<S, A>> = { ...options };

    if (!options$.name) {
        options$.name = 'Unknown';
    }

    if (!options$.contextName) {
        options$.contextName = `context(${options$.name})`;
    }

    if (typeof options$.debug === 'undefined') {
        options$.debug = true;
    }

    if (typeof options$.debug === 'boolean') {
        options$.debug = {
            actions: options$.debug,
            events: options$.debug,
            misc: options$.debug,
        };
    }

    if (!options$.initialState) {
        options$.initialState = INITIAL_STATE as any;
    }

    if (Record.isRecord(options$.initialState)) {
        throw new Error('createContext received a non "Record" initialState!');
    }

    return options$ as any;
};
